/**
 * Capabilities section. Materials card spans the full container width below
 * the title; Equipment and Tolerances/finishes sit side-by-side below it.
 */
import { SectionLabel } from "../layout/SectionLabel";

const MATERIALS = [
  {
    group: "Aluminum Alloys",
    items: ["6061-T6/T651", "7075-T6", "MIC-6 plate"],
  },
  {
    group: "Stainless Steel",
    items: ["303", "304","316 / 316L", "15-5 PH", "17-4 PH"],
  },
  {
    group: "Carbon & Alloy Steel",
    items: ["1018", "12L14", "1045", "4130", "4140 PH"],
  },
  {
    group: "Plastics",
    items: ["Delrin / Acetal", "Nylon", "Polycarbonate", "Ultem", "PEEK"],
  },
] as const;

const TOLERANCES = [
  { label: "Standard", value: "±0.005\"", note: "Most Milled Features" },
  { label: "Precision", value: "±0.001\"", note: "Critical Fits" },
  { label: "Surface finish", value: "32 µin Ra", note: "Typical" },
] as const;

const FINISHES = ["As-Machined", "Bead Blast", "Anodize-Ready", "Chem Film / Chromate-Ready"];

export function Capabilities() {
  return (
    <section
      id="capabilities"
      className="section-anchor mx-auto max-w-7xl px-4 pt-8 pb-32 sm:px-6 sm:pt-12 sm:pb-40"
    >
      <SectionLabel>Capabilities</SectionLabel>
      <h2 className="mt-4 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
      	Our Machine. Your Tolerance. 
      </h2>

      <div className="mt-12 space-y-6">
        {/* Materials — full width */}
        <div className="rounded-xl border border-bg-border bg-bg-card/85 backdrop-blur-[8px] p-6">
          <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-400">
            Materials
          </h3>
          <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
            {MATERIALS.map(({ group, items }) => (
              <div key={group}>
                <dt className="text-xl font-medium text-white">{group}</dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-slate-300">
                  {items.join(" · ")}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Equipment + Tolerances — side by side below Materials */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-bg-border bg-bg-card/85 backdrop-blur-[8px] p-6">
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-400">
              Equipment
            </h3>
            <div className="mt-5">
              <p className="text-base font-medium text-white">3-Axis VMC</p>
              <p className="mt-1 text-sm text-slate-300">
                Smart Machine Tool MINI · Fanuc 0i-MF Plus
              </p>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between border-t border-bg-border pt-3">
                  <dt className="text-slate-400">Work Envelope</dt>
                  <dd className="text-slate-200">22 × 16 × 10 in.</dd>
                </div>
                <div className="flex justify-between border-t border-bg-border pt-3">
                  <dt className="text-slate-400">Spindle</dt>
                  <dd className="text-slate-200">12,000 RPM · CAT 40 BP</dd>
                </div>
                <div className="flex justify-between border-t border-bg-border pt-3">
                  <dt className="text-slate-400">Tool Setting</dt>
                  <dd className="text-slate-200">Haimer Presetter</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="rounded-xl border border-bg-border bg-bg-card/85 backdrop-blur-[8px] p-6">
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-400">
              Tolerances &amp; finishes
            </h3>
            <div className="mt-5 grid grid-cols-1 overflow-hidden rounded-lg border border-bg-border sm:grid-cols-3">
              {TOLERANCES.map(({ label, value, note }) => (
                <div key={label} className="flex flex-col gap-1 bg-bg-card/85 backdrop-blur-[8px] px-4 py-4">
                  <span className="text-xs uppercase tracking-[0.16em] text-slate-400">{label}</span>
                  <span className="text-lg font-semibold text-white">{value}</span>
                  <span className="text-xs text-slate-400">{note}</span>
                </div>
              ))}
            </div>
            <div className="mt-5">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Finishes</p>
              <p className="mt-2 text-sm text-slate-200">{FINISHES.join(" · ")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
